import React, { useEffect, useMemo, useState } from 'react';
import { complianceStandardsApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, CheckCircle2, X, Search } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRY_OPTIONS = [
  { value: '', label: 'All countries' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'EU', label: 'European Union' },
];

const catLabelLib = (c) => ({ crane: 'Cranes', truck: 'Trucks', forklift: 'Forklifts', excavator: 'Excavators', lifting_gear: 'Lifting Gear', ewp: 'EWP', custom: 'Custom' }[c] || (c ? c.replace(/_/g, ' ') : ''));

const StandardsLibraryModal = ({ open, onClose, onImported }) => {
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const load = async (countryCode) => {
    setLoading(true);
    try {
      const { data } = await complianceStandardsApi.listLibrary(
        countryCode ? { country: countryCode } : undefined
      );
      setRows(data || []);
    } catch (e) {
      toast.error('Failed to load standards library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(country);
  }, [open, country]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.code} ${r.name} ${r.jurisdiction} ${(r.asset_categories || []).join(' ')}`
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const handleImport = async (row) => {
    setImportingId(row.id);
    try {
      const { data } = await complianceStandardsApi.importFromLibrary(row.id);
      toast.success(`Imported ${row.code}`);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, already_imported: true } : r))
      );
      onImported?.(data.standard);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 pt-[5vh]"
      onClick={onClose}
      data-testid="standards-library-modal"
    >
      <div
        className="bg-white border border-[#DAD5CA] rounded-xl max-w-5xl w-full max-h-[88vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#DAD5CA] flex items-start justify-between">
          <div>
            <h2 className="font-bold text-[19px] text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>International Standards Library</h2>
            <p className="text-[13px] text-[#6B6660] mt-0.5">Curated catalogue across six jurisdictions. Import a standard to add its requirements to your register.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="library-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-[#E6E2D9] bg-white flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-0.5 bg-[#F2F0EA] border border-[#E2DED4] rounded-full p-[3px] flex-wrap">
            {COUNTRY_OPTIONS.map((c) => (
              <button
                key={c.value || 'all'}
                onClick={() => setCountry(c.value)}
                className={`h-[26px] px-3 rounded-full text-[13px] transition-colors ${country === c.value ? 'bg-[#FFD108] text-black font-semibold shadow-sm' : 'text-[#3D3A35] font-medium hover:text-[#1A1A1A]'}`}
                data-testid={`library-filter-${c.value || 'all'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="ml-auto relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Code, name or category"
              className="h-9 pl-9 text-[13px]"
              data-testid="library-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto px-6 py-4 bg-[#FBFAF7]">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No standards match your filter.</div>
          ) : (
            <div className="space-y-2.5">
              {filtered.map((r) => (
                <div
                  key={r.id}
                  className={`bg-white rounded-[10px] p-[13px_14px] px-3.5 py-3.5 flex items-start gap-2.5 ${r.already_imported ? 'border border-[#A7F3D0]' : 'border border-[#DAD5CA]'}`}
                  data-testid={`library-row-${r.id}`}
                >
                  <div className="w-[30px] h-[22px] shrink-0 rounded bg-[#F6F4EF] border border-[#DAD5CA] flex items-center justify-center font-mono text-[11px] font-bold text-[#3D3A35]">
                    {r.country || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono font-bold text-[13px] text-[#1A1A1A]">{r.code}</span>
                      <span className="text-[14px] font-semibold text-[#1A1A1A]">{r.name}</span>
                    </div>
                    <p className="text-[12.5px] leading-[1.5] text-[#6B6660] mt-0.5 mb-2">{r.description}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-[#3D3A35] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[5px] px-2 py-0.5">{r.jurisdiction}</span>
                      {(r.asset_categories || []).map((cat) => (
                        <span key={cat} className="text-[11px] font-semibold text-[#3D3A35] bg-[#F6F4EF] border border-[#DAD5CA] rounded-[5px] px-2 py-0.5">{catLabelLib(cat)}</span>
                      ))}
                      <span className="font-mono text-[11px] text-[#6B6660] ml-1">{(r.required_certificates || []).length} cert · {(r.required_inspections || []).length} insp</span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    {r.already_imported ? (
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#047857] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2.5 py-1" data-testid={`library-inregister-${r.id}`}>
                        <CheckCircle2 className="w-[13px] h-[13px]" /> In register
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={importingId === r.id}
                        onClick={() => handleImport(r)}
                        className="h-[30px] rounded-[7px] border-[#C9C3B6] hover:bg-[#FFD108] hover:text-black hover:border-[#FFD108]"
                        data-testid={`library-import-${r.id}`}
                      >
                        {importingId === r.id ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
                        Import
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[#DAD5CA] flex items-center justify-between bg-white">
          <p className="text-[12px] text-[#6B6660]">
            {filtered.length} of {rows.length} standards · {rows.filter((r) => r.already_imported).length} already in your register
          </p>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default StandardsLibraryModal;
